import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { extractBoardDocumentSnapshot } from '../utils/boardSnapshot';
import { SERVER_ROOT } from 'env';

/**
 * Real-time co-edit sync over Socket.IO (tldraw document snapshots + presence).
 * Editors join room board:{boardId} and broadcast document patches.
 */
export function useBoardCollabSync({
  enabled,
  boardId,
  editorRef,
  canEdit,
  onRemoteSnapshot,
}) {
  const user = useSelector(state => state.user);
  const socketRef = useRef(null);
  const applyingRemoteRef = useRef(false);
  const lastSentRef = useRef('');

  useEffect(() => {
    if (!enabled || !canEdit || !boardId || !user?._id) return undefined;

    let cancelled = false;
    let socket = null;

    (async () => {
      try {
        const { io } = await import('socket.io-client');
        if (cancelled) return;
        const url = SERVER_ROOT.replace(/\/$/, '');
        socket = io(`${url}/board-collab`, {
          withCredentials: true,
          transports: ['websocket', 'polling'],
        });
        socketRef.current = socket;

        socket.on('connect', () => {
          socket.emit('BOARD_COLLAB_JOIN', {
            boardId: String(boardId),
            user: {
              id: String(user._id),
              username: user.username,
              profileImageUrl: user.profileImageUrl || '',
            },
          });
        });

        socket.on('BOARD_COLLAB_SNAPSHOT', payload => {
          if (!payload?.snapshot || String(payload.fromUserId) === String(user._id)) return;
          applyingRemoteRef.current = true;
          try {
            onRemoteSnapshot?.(payload.snapshot);
          } finally {
            setTimeout(() => {
              applyingRemoteRef.current = false;
            }, 50);
          }
        });
      } catch (err) {
        console.warn('Board collab sync unavailable', err?.message || err);
      }
    })();

    return () => {
      cancelled = true;
      if (socket) {
        socket.emit('BOARD_COLLAB_LEAVE', { boardId: String(boardId) });
        socket.disconnect();
      }
      socketRef.current = null;
    };
  }, [enabled, canEdit, boardId, user?._id, user?.username, user?.profileImageUrl, onRemoteSnapshot]);

  useEffect(() => {
    if (!enabled || !canEdit || !boardId) return undefined;
    const editor = editorRef.current;
    if (!editor) return undefined;

    let timer = null;
    const unsub = editor.store.listen(
      () => {
        if (applyingRemoteRef.current) return;
        clearTimeout(timer);
        timer = setTimeout(() => {
          const socket = socketRef.current;
          if (!socket?.connected) return;
          try {
            const snapshot = extractBoardDocumentSnapshot(editor);
            const key = JSON.stringify(snapshot);
            if (key === lastSentRef.current) return;
            lastSentRef.current = key;
            socket.emit('BOARD_COLLAB_SNAPSHOT', {
              boardId: String(boardId),
              snapshot,
            });
          } catch {
            /* ignore */
          }
        }, 120);
      },
      { source: 'user', scope: 'document' }
    );

    return () => {
      clearTimeout(timer);
      unsub?.();
    };
  }, [enabled, canEdit, boardId, editorRef]);
}
